import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import './ShopModal.css';
import { getRarityColor, getRarityClass, getRarityGlow } from '../utils/rarityUtils';
import ItemTooltipContent from './shared/ItemTooltipContent';
import TooltipBadge from './shared/TooltipBadge';
import { formatCurrency } from './shared/formatCompact';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

const ShopModal = ({ character, onClose, onCharacterUpdate }) => {
  const [shopData, setShopData] = useState(null);
  const [activeTab, setActiveTab] = useState('buy');
  const [message, setMessage] = useState({ text: '', type: 'info' });
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [playerGold, setPlayerGold] = useState(character?.gold || 0);
  const [playerInventory, setPlayerInventory] = useState([]);
  const [playerPouch, setPlayerPouch] = useState([]);
  const [charismaDiscount, setCharismaDiscount] = useState(0);
  const [charismaSellBonus, setCharismaSellBonus] = useState(0);
  const [tooltipItem, setTooltipItem] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const tooltipPinned = useRef(false);
  const [actionBusy, setActionBusy] = useState(false);

  // v639.2: Vendor buyback queue
  const [buyback, setBuyback] = useState([]);

  // Dismiss tooltip on tab change
  useEffect(() => { setTooltipItem(null); tooltipPinned.current = false; }, [activeTab]);

  // v639.8: Map a hovered item to the player's currently-equipped counterpart
  // so ItemTooltipContent can render stat-delta comparisons.
  const findEquippedComparable = useCallback((item) => {
    if (!item || !character?.equipment) return null;
    const eq = character.equipment;
    const t = item.type;
    if (t === 'weapon') return eq.weapon || null;
    if (t === 'shield') return eq.offhand || null;
    if (t === 'breastplate' || t === 'armor') return eq.breastplate || null;
    if (t === 'helm') return eq.helm || null;
    if (t === 'belt') return eq.belt || null;
    if (t === 'backpack') return eq.backpack || null;
    if (t === 'ring') return eq.ring_left || eq.ring_right || null;
    if (t === 'gloves') return eq.gloves_left || eq.gloves_right || null;
    if (t === 'grieves') return eq.grieves_left || eq.grieves_right || null;
    if (t === 'pauldrons') return eq.pauldrons_left || eq.pauldrons_right || null;
    return null;
  }, [character]);

  // Adjust tooltip if it overflows the viewport
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el || !tooltipItem) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    if (rect.bottom > viewportH - 8) {
      el.style.top = `${Math.max(8, viewportH - rect.height - 8)}px`;
    }
  });

  const handleMouseEnter = (e, item) => {
    if (tooltipPinned.current) return;
    showTooltip(e, item);
  };
  const handleMouseLeave = () => {
    if (tooltipPinned.current) return;
    setTooltipItem(null);
  };
  const handleItemClick = (e, item) => {
    if (e.target.closest('.shop-item-qty') || e.target.closest('.shop-buy-btn') || e.target.closest('.shop-sell-btn')) return;
    if (tooltipItem === item && tooltipPinned.current) {
      tooltipPinned.current = false;
      setTooltipItem(null);
    } else {
      tooltipPinned.current = true;
      showTooltip(e, item);
    }
  };
  const showTooltip = (e, item) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipWidth = 260;
    const x = Math.min(rect.left, window.innerWidth - tooltipWidth - 12);
    setTooltipItem(item);
    setTooltipPos({ x: Math.max(8, x), y: rect.bottom + 4 });
  };
  const handleContentClick = (e) => {
    if (tooltipPinned.current && !e.target.closest('.shop-item-row')) {
      tooltipPinned.current = false;
      setTooltipItem(null);
    }
  };

  // Load shop data on mount
  useEffect(() => {
    loadShop();
    refreshBuyback();
  }, []);

  // v639.2: Buyback fetch + repurchase
  const refreshBuyback = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/shop/buyback`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setBuyback(data.entries || []);
    } catch (err) { /* non-fatal */ }
  };

  const handleBuyback = useCallback(async (entryId) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/shop/buyback`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryId }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.gold != null) setPlayerGold(data.gold);
        if (data.inventory) setPlayerInventory(data.inventory);
        if (data.pouch) setPlayerPouch(data.pouch);
        setMessage({ text: data.message, type: 'success' });
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Buyback failed', type: 'error' });
      }
      refreshBuyback();
    } catch (err) {
      setMessage({ text: 'Buyback failed', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, onCharacterUpdate]);

  // Sync gold from character prop
  useEffect(() => {
    if (character) {
      setPlayerGold(character.gold || 0);
      try {
        const inv = typeof character.inventory === 'string' 
          ? JSON.parse(character.inventory) 
          : (character.inventory || []);
        setPlayerInventory(inv);
        const pch = typeof character.pouch === 'string'
          ? JSON.parse(character.pouch)
          : (character.pouch || []);
        setPlayerPouch(Array.isArray(pch) ? pch : []);
      } catch {
        setPlayerInventory([]);
        setPlayerPouch([]);
      }
    }
  }, [character]);

  const loadShop = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/shop/open`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setShopData(data);
        setCharismaDiscount(data.charismaDiscount || 0);
        setCharismaSellBonus(data.charismaSellBonus || 0);
        setMessage({ text: `Welcome to ${data.shopName}!`, type: 'info' });
      } else {
        let errMsg = 'Failed to open shop';
        try {
          const err = await response.json();
          errMsg = err.error || errMsg;
        } catch (parseErr) {
          errMsg = `Server error (${response.status})`;
        }
        setMessage({ text: errMsg, type: 'error' });
      }
    } catch (error) {
      console.error('Error loading shop:', error);
      setMessage({ text: 'Error connecting to shop: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = useCallback(async (itemName, cost, overrideQty) => {
    if (actionBusy) return;
    const qty = overrideQty || quantities[itemName] || 1;
    const totalCost = cost * qty;

    if (playerGold < totalCost) {
      setMessage({ text: `Not enough gold! Need ${totalCost}, have ${playerGold}.`, type: 'error' });
      return;
    }

    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/shop/buy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ itemName, quantity: qty })
      });

      const data = await response.json();
      
      if (data.success) {
        setPlayerGold(data.gold);
        if (data.inventory) {
          setPlayerInventory(data.inventory);
        }
        setMessage({ text: data.message, type: 'success' });
        // Reset quantity
        setQuantities(prev => ({ ...prev, [itemName]: 1 }));
        // Notify parent to refresh character data
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Purchase failed', type: 'error' });
      }
    } catch (error) {
      console.error('Error buying item:', error);
      setMessage({ text: 'Error processing purchase', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  }, [quantities, playerGold, onCharacterUpdate, actionBusy]);

  const handleSell = useCallback(async (itemName, overrideQty, source = 'inventory') => {
    if (actionBusy) return;
    const qty = overrideQty || quantities[`sell_${itemName}`] || 1;

    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/shop/sell`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ itemName, quantity: qty, source })
      });

      const data = await response.json();
      
      if (data.success) {
        setPlayerGold(data.gold);
        if (data.inventory) {
          setPlayerInventory(data.inventory);
        }
        if (data.pouch) {
          setPlayerPouch(Array.isArray(data.pouch) ? data.pouch : []);
        }
        setMessage({ text: data.message, type: 'success' });
        setQuantities(prev => ({ ...prev, [`sell_${itemName}`]: 1 }));
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Sale failed', type: 'error' });
      }
    } catch (error) {
      console.error('Error selling item:', error);
      setMessage({ text: 'Error processing sale', type: 'error' });
    } finally {
      setActionBusy(false);
      refreshBuyback();
    }
  }, [quantities, onCharacterUpdate, actionBusy]);

  const handleQuantityChange = (key, value) => {
    const num = parseInt(value) || 1;
    setQuantities(prev => ({ ...prev, [key]: Math.max(1, Math.min(99, num)) }));
  };

  // v639.1: Bulk sell-all-junk by rarity tier (skips locked, quest, gear,
  // tomes, and recipe scrolls).
  const handleBulkSell = useCallback(async (maxRarity) => {
    if (actionBusy) return;
    const tierLabel = { common: 'Common', uncommon: 'Common + Uncommon', rare: 'Common + Uncommon + Rare' }[maxRarity] || maxRarity;
    if (!window.confirm(`Sell ALL ${tierLabel} items in your inventory and pouch?\n\n⭐ Locked items, equipment, tomes, and quest items are skipped.`)) {
      return;
    }
    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      // Sell from inventory then pouch in sequence so the player gets one
      // combined receipt and we don't need a server-side multi-source path.
      let totalGold = 0;
      let totalItems = 0;
      let lastInv = null, lastPouch = null;
      for (const source of ['inventory', 'pouch']) {
        const res = await fetch(`${API_BASE_URL}/api/shop/sell-bulk`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ maxRarity, source }),
        });
        const data = await res.json();
        if (data.success) {
          totalGold += data.totalEarned || 0;
          totalItems += data.itemCount || 0;
          if (data.inventory) lastInv = data.inventory;
          if (data.pouch) lastPouch = data.pouch;
          if (data.gold != null) setPlayerGold(data.gold);
        }
        // ignore "nothing to sell" errors silently for the second pass
      }
      if (lastInv) setPlayerInventory(lastInv);
      if (lastPouch) setPlayerPouch(lastPouch);
      if (totalItems > 0) {
        setMessage({ text: `Bulk sold ${totalItems} item${totalItems === 1 ? '' : 's'} for ${totalGold.toLocaleString()} gp.`, type: 'success' });
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: 'Nothing to sell at that tier (locked/gear/tomes are skipped).', type: 'error' });
      }
    } catch (err) {
      console.error('Bulk sell error:', err);
      setMessage({ text: 'Bulk sell failed', type: 'error' });
    } finally {
      setActionBusy(false);
      refreshBuyback();
    }
  }, [actionBusy, onCharacterUpdate]);  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Get the count of an item in player inventory
  const getOwnedCount = (itemName) => {
    const item = playerInventory.find(i => i.name === itemName);
    return item ? (item.quantity || 1) : 0;
  };

  // Classes that can learn spells from tomes
  const CASTER_CLASSES = ['Mage', 'Cleric', 'Psion', 'Druid'];
  const canLearnSpells = CASTER_CLASSES.includes(character?.class);

  // Calculate sell price: 1/4 of buy cost + CHA bonus
  const calcSellPrice = (item) => {
    const base = Math.max(1, Math.floor((item.cost || 1) / 4));
    return Math.max(1, Math.round(base * (1 + charismaSellBonus / 100)));
  };

  if (loading) {
    return (
      <div className="shop-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
        <div className="shop-modal" onClick={e => e.stopPropagation()}>
          <div className="shop-header">
            <h2>Loading shop...</h2>
          </div>
          <div className="shop-content">
            <div className="shop-empty">Loading inventory...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-overlay" onClick={onClose} onKeyDown={handleKeyDown} tabIndex={-1}>
      <div className="shop-modal" onClick={e => { e.stopPropagation(); handleContentClick(e); }}>
        {/* Header */}
        <div className="shop-header">
          <h2>{shopData?.shopName || 'Shop'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {charismaDiscount > 0 && (
              <div className="shop-cha-badge" title={`Your Charisma gives you ${charismaDiscount}% better buy prices and ${charismaSellBonus}% better sell prices`}>
                🐾 {charismaDiscount}% CHA discount
                <TooltipBadge id="shop.cha-discount" />
              </div>
            )}
            <div className="shop-gold">
              <span className="shop-gold-icon"></span>
              <span>{formatCurrency(playerGold)}</span>
            </div>
            <button className="shop-close-btn" onClick={onClose} title="Close (Esc)">✖</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="shop-tabs">
          <button 
            className={`shop-tab ${activeTab === 'buy' ? 'active' : ''}`}
            onClick={() => setActiveTab('buy')}
          >
            🛒 Buy
          </button>
          <button 
            className={`shop-tab ${activeTab === 'sell' ? 'active' : ''}`}
            onClick={() => setActiveTab('sell')}
          >
            💰 Sell
          </button>
          <button
            className={`shop-tab ${activeTab === 'buyback' ? 'active' : ''}`}
            onClick={() => { setActiveTab('buyback'); refreshBuyback(); }}
            title="Buy back items you recently sold"
          >
            ↩️ Buyback{buyback.length > 0 ? ` (${buyback.length})` : ''}
          </button>
        </div>

        {/* Content */}
        <div className="shop-content">
          {activeTab === 'buy' ? (
            shopData?.items?.length > 0 ? (
              shopData.items.map((item, idx) => {
                const qty = quantities[item.name] || 1;
                const totalCost = item.cost * qty;
                const canAfford = playerGold >= totalCost;
                const owned = getOwnedCount(item.name);
                const charLevel = character?.level || 1;
                const itemLevel = item.level || 1;
                const meetsLevel = charLevel >= itemLevel;
                const canBuy = canAfford && meetsLevel;

                return (
                  <div key={idx} className={`shop-item-row${!meetsLevel ? ' shop-item-locked' : ''}`}
                    onMouseEnter={(e) => handleMouseEnter(e, item)}
                    onMouseLeave={handleMouseLeave}
                    onClick={(e) => handleItemClick(e, item)}
                  >
                    <div className="shop-item-info">
                      <div className={`shop-item-name ${getRarityClass(item)}`} style={{ color: getRarityColor(item), textShadow: getRarityGlow(item) }}>
                        {item.name}
                        {itemLevel > 1 && <span className={`shop-item-level${!meetsLevel ? ' level-too-high' : ''}`}>Lv {itemLevel}</span>}
                        {owned > 0 && <span className="shop-item-owned">(own: {owned})</span>}
                        {item.type === 'tome' && item.spellPrereqs && canLearnSpells && (
                          item.meetsAllPrereqs
                            ? <span className="shop-tome-badge met" title="You meet all seed requirements">✓ Ready</span>
                            : <span className="shop-tome-badge unmet" title="Missing seed requirements">✗ Seeds</span>
                        )}
                      </div>
                      <div className="shop-item-desc">
                        {item.description}
                        {!meetsLevel && <span className="shop-level-warning"> ⚠️ Requires Level {itemLevel}</span>}
                        {item.type === 'tome' && !canLearnSpells && <span className="shop-level-warning"> ⚠️ Your class cannot learn magic</span>}
                      </div>
                    </div>
                    <div className="shop-item-price">
                      {item.originalCost && item.originalCost !== item.cost ? (
                        <>
                          <span className="shop-price-original">{(qty > 1 ? item.originalCost * qty : item.originalCost).toLocaleString()}</span>
                          {' '}{totalCost.toLocaleString()}
                        </>
                      ) : (
                        qty > 1 ? totalCost.toLocaleString() : item.cost.toLocaleString()
                      )} gp
                      {/* v642.10: explain why the price is lower than
                          the original — surfaces the CHA discount tooltip
                          right at the point of sale. */}
                      {item.originalCost && item.originalCost !== item.cost && (
                        <TooltipBadge id="shop.cha-discount" size={11} />
                      )}
                    </div>
                    <div className="shop-item-qty">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={qty}
                        onChange={e => handleQuantityChange(item.name, e.target.value)}
                        disabled={!meetsLevel}
                      />
                    </div>
                    <button
                      className="shop-buy-btn"
                      disabled={!canBuy}
                      onClick={(e) => {
                        if ((e.ctrlKey || e.metaKey) && meetsLevel) {
                          const maxQty = Math.min(99, Math.floor(playerGold / item.cost));
                          if (maxQty > 0) handleBuy(item.name, item.cost, maxQty);
                        } else {
                          handleBuy(item.name, item.cost);
                        }
                      }}
                      title={!meetsLevel ? `Requires Level ${itemLevel}` : canAfford ? `Buy ${qty}x for ${totalCost} gp. Ctrl+Click to buy max` : 'Not enough gold'}
                    >
                      Buy
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="shop-empty">This shop has nothing for sale.</div>
            )
          ) : activeTab === 'sell' ? (
            /* Sell Tab */
            (() => {
              const sellList = [
                ...playerInventory.map(it => ({ ...it, _source: 'inventory' })),
                ...playerPouch.map(it => ({ ...it, _source: 'pouch' })),
              ];
              return (<>
                {/* v639.1: Bulk sell controls */}
                <div className="shop-bulk-bar" style={{
                  display: 'flex', gap: 8, padding: '8px 10px', marginBottom: 8,
                  background: 'rgba(40,30,20,0.55)', border: '1px solid #5a4a2a',
                  borderRadius: 6, alignItems: 'center', flexWrap: 'wrap'
                }}>
                  <span style={{ color: '#c9a84c', fontSize: '0.85em', fontWeight: 'bold' }}>
                    🧹 Bulk sell:
                  </span>
                  <button
                    className="shop-bulk-btn"
                    onClick={() => handleBulkSell('common')}
                    disabled={actionBusy}
                    title="Sell every Common-rarity item (skips locked, gear, tomes, quest)"
                    style={{ background: '#3a3a3a', color: '#bbb', border: '1px solid #9d9d9d', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: '0.85em' }}
                  >Common</button>
                  <button
                    className="shop-bulk-btn"
                    onClick={() => handleBulkSell('uncommon')}
                    disabled={actionBusy}
                    title="Sell every Common + Uncommon item (skips locked, gear, tomes, quest)"
                    style={{ background: '#1a3a1a', color: '#1eff00', border: '1px solid #1eff00', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: '0.85em' }}
                  >+ Uncommon</button>
                  <button
                    className="shop-bulk-btn"
                    onClick={() => handleBulkSell('rare')}
                    disabled={actionBusy}
                    title="Sell every Common + Uncommon + Rare item (skips locked, gear, tomes, quest)"
                    style={{ background: '#1a2a3a', color: '#0070dd', border: '1px solid #0070dd', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: '0.85em' }}
                  >+ Rare</button>
                  <span style={{ color: '#888', fontSize: '0.75em', marginLeft: 'auto' }}>
                    Lock (⭐) items in your inventory to keep them safe.
                  </span>
                </div>
                {sellList.length > 0 ? (
              sellList.map((item, idx) => {
                const sellPrice = calcSellPrice(item);
                const qty = quantities[`sell_${item.name}`] || 1;
                const available = item.quantity || 1;
                const totalEarned = sellPrice * qty;

                return (
                  <div key={idx} className="shop-item-row"
                    onMouseEnter={(e) => handleMouseEnter(e, item)}
                    onMouseLeave={handleMouseLeave}
                    onClick={(e) => handleItemClick(e, item)}
                  >
                    <div className="shop-item-info">
                      <div className={`shop-item-name ${getRarityClass(item)}`} style={{ color: getRarityColor(item), textShadow: getRarityGlow(item) }}>
                        {item.name}
                        {available > 1 && <span className="shop-item-owned">(x{available})</span>}
                      </div>
                      <div className="shop-item-desc">{item.description || item.type || ''}</div>
                    </div>
                    <div className={`shop-item-price sell-price${charismaSellBonus > 0 ? ' cha-boosted' : ''}`}>
                      +{qty > 1 ? totalEarned.toLocaleString() : sellPrice.toLocaleString()} gp
                      {/* v642.10: CHA badge on the sell side too — explains
                          why the offer is higher than the listed base price. */}
                      {charismaSellBonus > 0 && (
                        <TooltipBadge id="shop.cha-discount" size={11} />
                      )}
                    </div>
                    <div className="shop-item-qty">
                      <input
                        type="number"
                        min="1"
                        max={available}
                        value={Math.min(qty, available)}
                        onChange={e => handleQuantityChange(`sell_${item.name}`, e.target.value)}
                      />
                    </div>
                    <button
                      className="shop-sell-btn"
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          handleSell(item.name, available, item._source);
                        } else {
                          handleSell(item.name, undefined, item._source);
                        }
                      }}
                      title={available > 1 ? `Sell ${Math.min(qty, available)}. Ctrl+Click to sell all ${available}` : 'Sell'}
                    >
                      Sell{available > 1 ? '' : ''}
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="shop-empty">You have nothing to sell.</div>
            )}
              </>);
            })()
          ) : null}
          {/* v639.2: Buyback Tab */}
          {activeTab === 'buyback' && (
            <>
              {buyback.length === 0 ? (
                <div className="shop-empty">No recent sales available for buyback. Items expire 30 minutes after sale.</div>
              ) : (
                <>
                  <div style={{ padding: '6px 10px', color: '#c9a84c', fontSize: '0.8em', borderBottom: '1px solid #5a4a2a' }}>
                    Last {buyback.length} item{buyback.length === 1 ? '' : 's'} sold — repurchase at the same price.
                  </div>
                  {buyback.map((entry) => {
                    const item = entry.item || { name: entry.name };
                    const canAfford = playerGold >= entry.totalCost;
                    return (
                      <div key={entry.id} className="shop-item-row"
                        onMouseEnter={(e) => handleMouseEnter(e, item)}
                        onMouseLeave={handleMouseLeave}
                        onClick={(e) => handleItemClick(e, item)}
                      >
                        <div className="shop-item-info">
                          <div className={`shop-item-name ${getRarityClass(item)}`} style={{ color: getRarityColor(item), textShadow: getRarityGlow(item) }}>
                            {entry.name}
                            {entry.quantity > 1 && <span className="shop-item-owned">(x{entry.quantity})</span>}
                          </div>
                          <div className="shop-item-desc">
                            {item.description || item.type || ''}
                            {entry.source === 'pouch' ? ' · from pouch' : ''}
                          </div>
                        </div>
                        <div className="shop-item-price">
                          {entry.totalCost.toLocaleString()} gp
                        </div>
                        <button
                          className="shop-buy-btn"
                          onClick={() => handleBuyback(entry.id)}
                          disabled={!canAfford || actionBusy}
                          title={canAfford ? `Buy back for ${entry.totalCost} gp` : 'Not enough gold'}
                          style={{ minWidth: 90 }}
                        >Buy Back</button>
                      </div>
                    );
                  })}
                </>
              )}equippedItem={findEquippedComparable(tooltipItem)} 
            </>
          )}
        </div>

        {/* Message */}
        <div className={`shop-message ${message.type}`}>
          {message.text}
        </div>
        <div className="shop-hint">💡 Ctrl+Click a button to {activeTab === 'sell' ? 'sell entire stack' : 'buy max affordable'}</div>
      </div>

      {/* Shared Item Tooltip */}
      {tooltipItem && (
        <div
          className="item-tooltip-popup"
          ref={tooltipRef}
          style={{ position: 'fixed', left: tooltipPos.x, top: tooltipPos.y, zIndex: 10001 }}
        >
          <ItemTooltipContent item={tooltipItem} canLearnSpells={canLearnSpells} />
        </div>
      )}
    </div>
  );
};

export default ShopModal;
